/**
 * Envelopes for the card, each cut and folded from one sheet of US Letter
 * (asked for 2026-10-17: "an envelope that could fit the card ... may use glue,
 * let's try cutting, provide many options ... letter paper with lines drawn so
 * the user can cut or fold"). The earlier study (foldmycard OpenCode/, 2026-09-30
 * and 10-01) found that an upright pocket for this card cannot come out of one
 * Letter sheet - the card lies in its envelope sideways - and designed a no-cut
 * pocket; that one is here as "No scissors", with the pocket's sides glued.
 *
 * Every envelope is worked out from the closed card's own size (the template's
 * front view): 3 mm of air all round, as the study derived from the card's
 * thickness. Geometry only, in millimetres on the sheet as printed (side 1, the
 * inside of the envelope, x right, y down); the drawings are in sheet.tsx and
 * the checks in designs.test.ts.
 *
 * The cut-out envelopes are one family (the commercial "side seam" envelope):
 * a back panel the card lies on; two side flaps folded in; a bottom flap glued
 * onto them, which is the front of the flap side; and a top flap that closes
 * it, its shape the option - pointed (Classic), straight, rounded, a heart tip,
 * scalloped. Each bottom flap is made just deep enough that, folded, the flaps
 * hide the card everywhere (checked), the glue sits where the bottom flap lies
 * over the side flaps, and the top flap's tip lands on the bottom flap for a
 * dab of glue or the round seal sticker printed beside the envelope.
 *
 * The pillow is a tube with curved ends that push in - one glued seam. Its
 * creases are curves, so it is scored with a pen rather than folded on a ruler.
 */
import { apply, compose, invert, type Affine } from '@foldmycard/scene';
import type { StringKey } from '../strings.ts';

export type Pt = [number, number];
export type EnvelopeId = 'classic' | 'straight' | 'rounded' | 'heart' | 'scallop' | 'pillow' | 'pocket';
export const ENVELOPES: EnvelopeId[] = ['classic', 'straight', 'rounded', 'heart', 'scallop', 'pillow', 'pocket'];

/** Air around the card, mm each side: ten times the closed card's thickness (the 2026-09-30 study). */
export const AIR = 3;
/** Nothing to cut or fold within this of the sheet's edge: most printers leave it white. */
export const MARGIN = 6;
const SEAM = 12;

export interface Flap {
  id: string;
  /** The flap's outline on the sheet, hinge included. */
  poly: Pt[];
  /** The fold it turns on. */
  hinge: [Pt, Pt];
}

/**
 * One view of the finished envelope, as layers: each a region of one side of
 * the sheet (in that side's own page coordinates - side 2 as printed, read
 * from its own face) and where it lands in the view.
 */
export interface View {
  outline: Pt[];
  layers: { side: 1 | 2; clip: Pt[]; toView: Affine }[];
}

export interface Envelope {
  id: EnvelopeId;
  sheet: { w: number; h: number };
  /** The closed card as it lies in the envelope (mm). */
  card: { w: number; h: number };
  /** Lines to cut (closed outlines), on side 1. None: nothing to cut. */
  cut: Pt[][];
  /** Fold lines on side 1, each with the step that folds it. */
  folds: { line: Pt[]; step: number; curved?: boolean }[];
  /** Where glue goes, on side 1. */
  glue: Pt[][];
  /** Where the card lies, on side 1. */
  cardAt: Pt[];
  /** The round seal sticker to cut out, if there is room for one (side-1 coordinates). */
  seal: { c: Pt; r: number } | null;
  /** The finished envelope: the side that takes the name, and the other side. */
  address: View;
  back: View;
  /** Where the name goes in the address view, and how much room it has. */
  nameAt: { c: Pt; w: number; h: number };
  /** Where the seal (or the closing dab) shows in the back view, if anywhere. */
  sealAt: Pt | null;
  /** A heart printed on the outside of the flap's heart tip, in the back view (the cut follows only its lower half). */
  tipArt: Pt[] | null;
  /** The finished envelope's size. */
  closed: { w: number; h: number };
  /** Inside, where the card goes. */
  inside: { w: number; h: number };
  scissors: boolean;
  steps: StringKey[];
}

/* ------------------------------------------------------------ geometry */

const SHEET = { w: 215.9, h: 279.4 };
const mirrorX = (w: number): Affine => [-1, 0, 0, 1, w, 0];
const translate = (x: number, y: number): Affine => [1, 0, 0, 1, x, y];

/** The reflection across the line through a and b. */
export function reflectAcross([a, b]: [Pt, Pt]): Affine {
  const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy);
  const ux = dx / L, uy = dy / L;
  // R = 2uu^T - I, about the point a
  const m00 = 2 * ux * ux - 1, m01 = 2 * ux * uy, m11 = 2 * uy * uy - 1;
  return [m00, m01, m01, m11, a[0] - (m00 * a[0] + m01 * a[1]), a[1] - (m01 * a[0] + m11 * a[1])];
}

export const area = (p: Pt[]) => Math.abs(p.reduce((s, a, i) => {
  const b = p[(i + 1) % p.length];
  return s + a[0] * b[1] - a[1] * b[0];
}, 0)) / 2;

export function inside([x, y]: Pt, poly: Pt[]) {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

/** The part of `subject` inside the convex polygon `clip` (Sutherland-Hodgman). */
export function clipConvex(subject: Pt[], clip: Pt[]): Pt[] {
  // orient the clip counter-clockwise in the y-down plane's maths sense
  const signed = clip.reduce((s, a, i) => { const b = clip[(i + 1) % clip.length]; return s + a[0] * b[1] - a[1] * b[0]; }, 0);
  const c = signed < 0 ? [...clip].reverse() : clip;
  let out = subject;
  for (let i = 0; i < c.length && out.length; i++) {
    const a = c[i], b = c[(i + 1) % c.length];
    const side = (p: Pt) => (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
    const input = out;
    out = [];
    for (let j = 0; j < input.length; j++) {
      const p = input[j], q = input[(j + 1) % input.length];
      const sp = side(p), sq = side(q);
      if (sp >= 0) out.push(p);
      if ((sp >= 0) !== (sq >= 0)) {
        const k = sp / (sp - sq);
        out.push([p[0] + (q[0] - p[0]) * k, p[1] + (q[1] - p[1]) * k]);
      }
    }
  }
  return out;
}

/** Points along an arc of an ellipse centred at c, from angle a0 to a1 (radians), n steps. */
function arc(c: Pt, rx: number, ry: number, a0: number, a1: number, n = 24): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    out.push([c[0] + rx * Math.cos(a), c[1] + ry * Math.sin(a)]);
  }
  return out;
}
/** Points along the circular arc from a to b that bulges by `sag` to the left of a->b (negative: to the right). */
function sagArc(a: Pt, b: Pt, sag: number, n = 20): Pt[] {
  const dx = b[0] - a[0], dy = b[1] - a[1], c = Math.hypot(dx, dy);
  const nx = dy / c, ny = -dx / c;              // the left normal, y down
  const out: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    // a parabola is close enough to a shallow arc - and its sagitta is exact
    const s = 4 * sag * t * (1 - t);
    out.push([a[0] + dx * t + nx * s, a[1] + dy * t + ny * s]);
  }
  return out;
}
/** A polygon shrunk towards its middle by about d mm (for glue a little inside its edges). */
function shrink(p: Pt[], d: number): Pt[] {
  const cx = p.reduce((s, q) => s + q[0], 0) / p.length, cy = p.reduce((s, q) => s + q[1], 0) / p.length;
  const r = Math.max(...p.map((q) => Math.hypot(q[0] - cx, q[1] - cy)));
  const k = Math.max(0, 1 - d / r);
  return p.map(([x, y]) => [cx + (x - cx) * k, cy + (y - cy) * k]);
}
const dedupe = (p: Pt[]) => p.filter((q, i) => {
  const r = p[(i + p.length - 1) % p.length];
  return Math.hypot(q[0] - r[0], q[1] - r[1]) > 1e-6;
});

/* ---------------------------------------------- the cut-out envelopes */

type TopStyle = 'v' | 'straight' | 'round' | 'heart' | 'scallop';
const TOP: Record<Exclude<EnvelopeId, 'pillow' | 'pocket'>, TopStyle> = {
  classic: 'v', straight: 'straight', rounded: 'round', heart: 'heart', scallop: 'scallop',
};

/**
 * The top flap's outline beyond its hinge, from (0,0) round to (W,0), in the
 * envelope's own frame (the hinge along y = 0, the flap at y < 0), depth t.
 */
function topFlap(style: TopStyle, W: number, t: number): Pt[] {
  const r = Math.min(8, t / 4);
  switch (style) {
    case 'v':
      return [[0, 0], [W / 2, -t], [W, 0]];
    case 'straight': {
      const inset = Math.min(10, W * 0.06);
      return [[0, 0], ...arc([inset + r, -t + r], r, r, Math.PI, Math.PI * 1.5, 8), ...arc([W - inset - r, -t + r], r, r, Math.PI * 1.5, Math.PI * 2, 8), [W, 0]];
    }
    case 'round':
      return arc([W / 2, 0], W / 2, t, Math.PI, Math.PI * 2, 40);
    case 'scallop': {
      // an odd number of scallops, so that one is in the middle, where the flap is sealed
      const inset = Math.min(10, W * 0.06), n = 2 * Math.max(2, Math.round((W - 2 * inset) / 44)) + 1;
      const w = (W - 2 * inset) / n, out: Pt[] = [[0, 0], [inset, -t + w / 2]];
      for (let i = 0; i < n; i++) out.push(...arc([inset + w * (i + 0.5), -t + w / 2], w / 2, w / 2, Math.PI, Math.PI * 2, 10).slice(1));
      out.push([W, 0]);
      return out;
    }
    case 'heart':
      return [[0, 0], ...heartTip(W, t), [W, 0]];
  }
}

/**
 * The heart at the top flap's tip: its lower curve, from its widest point on the
 * left round its point (at depth t) to its widest point on the right. The flap's
 * straight sides run from the hinge's ends to those widest points.
 */
export function heartTip(W: number, t: number): Pt[] {
  const at = heartCurve(W, t);
  const left: Pt[] = [];
  for (let i = 0; i <= 24; i++) left.push(at(-Math.PI / 2 - (Math.PI / 2) * (i / 24)));   // widest left .. the point
  const right = left.slice(0, -1).reverse().map(([x, y]): Pt => [W - x, y]);
  return [...left, ...right];
}
/** The whole heart, lobes too, unfolded (flap at y < 0). */
export function heartShape(W: number, t: number): Pt[] {
  const at = heartCurve(W, t);
  return Array.from({ length: 72 }, (_, i) => at((Math.PI * 2 * i) / 72));
}
function heartCurve(W: number, t: number) {
  // a third of the width: wider, and the flap's sides run so flat that the corners would need more paper than the sheet has
  const hw = W * 0.3, hh = hw * 0.92;
  // the classic heart curve: x = 16 sin^3 u, y = 13 cos u - 5 cos 2u - 2 cos 3u - cos 4u (its point at u = pi, y = -17)
  const at = (u: number): Pt => {
    const x = 16 * Math.sin(u) ** 3;
    const y = 13 * Math.cos(u) - 5 * Math.cos(2 * u) - 2 * Math.cos(3 * u) - Math.cos(4 * u);
    return [W / 2 + (x / 32) * hw, -t + ((y + 17) / 29) * hh];   // the point at -t, the lobes towards the hinge
  };
  return at;
}

/**
 * A side flap beyond the hinge x = 0 (flap at x < 0), from (0,H) up to (0,0):
 * square at the top, so that with any top flap the corners by the opening are
 * covered, and tapered at the bottom; or rounded.
 */
function sideFlap(round: boolean, H: number, s: number): Pt[] {
  if (round) return arc([0, H / 2], s, H / 2, Math.PI / 2, Math.PI * 1.5, 30);
  const tap = Math.min(s * 0.9, H * 0.22);
  return [[0, H], [-s, H - tap], [-s, 0], [0, 0]];
}

/** The bottom flap beyond the hinge y = H (flap at y > H), from (W,H) round to (0,H). */
function bottomFlap(W: number, H: number, b: number, inset: number): Pt[] {
  const r = Math.min(6, b / 5);
  return [[W, H], ...arc([W - inset - r, H + b - r], r, r, 0, Math.PI / 2, 6), ...arc([inset + r, H + b - r], r, r, Math.PI / 2, Math.PI, 6), [0, H]];
}

/** Every point of the panel (inset a little) under at least one folded flap. */
function covered(panel: { W: number; H: number }, folded: Pt[][]) {
  for (let i = 1; i < 40; i++) {
    for (let j = 1; j < 30; j++) {
      const p: Pt = [(panel.W * i) / 40, (panel.H * j) / 30];
      if (!folded.some((f) => inside(p, f))) return false;
    }
  }
  return true;
}

/** Where a shape fits on the sheet: the blank's box, placed in the middle - or turned a quarter, if only that fits. */
function place(minX: number, minY: number, maxX: number, maxY: number): Affine | null {
  const w = maxX - minX, h = maxY - minY;
  const room = (a: number, b: number) => a <= SHEET.w - 2 * MARGIN + 1e-9 && b <= SHEET.h - 2 * MARGIN + 1e-9;
  if (room(w, h)) return translate((SHEET.w - w) / 2 - minX, (SHEET.h - h) / 2 - minY);
  if (room(h, w)) {
    // (x, y) -> (-y, x), then into the middle
    const turn: Affine = [0, 1, -1, 0, 0, 0];
    return compose(translate((SHEET.w - h) / 2 + maxY, (SHEET.h - w) / 2 - minX), turn);
  }
  return null;
}

/** A spot for the round seal sticker: off the envelope, on the sheet. */
function sealSpot(blank: Pt[], r: number): Pt | null {
  let best: Pt | null = null, bestD = -1;
  const pad = r + 3;
  for (let x = MARGIN + pad; x <= SHEET.w - MARGIN - pad; x += 2) {
    for (let y = MARGIN + pad; y <= SHEET.h - MARGIN - pad; y += 2) {
      if (inside([x, y], blank)) continue;
      let d = Infinity;
      for (let i = 0; i < blank.length; i++) {
        const a = blank[i], b = blank[(i + 1) % blank.length];
        const ex = b[0] - a[0], ey = b[1] - a[1];
        const k = Math.max(0, Math.min(1, ((x - a[0]) * ex + (y - a[1]) * ey) / (ex * ex + ey * ey || 1)));
        d = Math.min(d, Math.hypot(x - a[0] - k * ex, y - a[1] - k * ey));
      }
      if (d >= pad && d > bestD) { bestD = d; best = [x, y]; }
    }
  }
  return best;
}

function cutOut(id: Exclude<EnvelopeId, 'pillow' | 'pocket'>, card: { w: number; h: number }): Envelope | null {
  const H = card.h + 2 * AIR, style = TOP[id];
  const avail = SHEET.h - 2 * MARGIN;
  const t = Math.min(H * (style === 'v' || style === 'heart' ? 0.62 : 0.5), (avail - H) * 0.45);
  const env = cutOutAt(id, card, t);
  if (env || (style !== 'v' && style !== 'heart')) return env;
  // a card wider than the classic (the purse, the surprise card, 2026-10-18): the pointed flap
  // that deep leaves the bottom flap no room on the sheet - a shallower point leaves it more
  for (let d = Math.floor(t) - 1; d >= H * 0.4; d--) {
    const e = cutOutAt(id, card, d);
    if (e) return e;
  }
  return null;
}

function cutOutAt(id: Exclude<EnvelopeId, 'pillow' | 'pocket'>, card: { w: number; h: number }, t: number): Envelope | null {
  const W = card.w + 2 * AIR, H = card.h + 2 * AIR;
  const style = TOP[id];
  const round = style === 'round';
  const avail = { w: SHEET.w - 2 * MARGIN, h: SHEET.h - 2 * MARGIN };
  // side flaps: as wide as the sheet allows, up to two fifths of the height
  const s = Math.min((avail.w - W) / 2, H * 0.42, 40);
  if (s < 10) return null;
  const inset = s * 0.35;
  const top = topFlap(style, W, t);
  const left = sideFlap(round, H, s);
  // the left flap mirrored, and run from (W,0) down to (W,H) like the rest of the outline
  const right = left.map(([x, y]): Pt => [W - x, y]).reverse();
  const panelPoly: Pt[] = [[0, 0], [W, 0], [W, H], [0, H]];
  const hinges = { top: [[0, 0], [W, 0]] as [Pt, Pt], left: [[0, H], [0, 0]] as [Pt, Pt], right: [[W, 0], [W, H]] as [Pt, Pt] };
  const foldOf = (poly: Pt[], hinge: [Pt, Pt]) => poly.map((p) => apply(reflectAcross(hinge), p));
  // the bottom flap: just deep enough that the folded flaps hide the card everywhere, 4 mm more for sure
  const fixed = [foldOf(top, hinges.top), foldOf(left, hinges.left), foldOf(right, hinges.right)];
  // where the top flap is closed: the deepest point of its middle with room for a 7 mm dab of glue (or the seal)
  const DAB = 7;
  const fitsDab = (d: number) => arc([W / 2, d], DAB, DAB, 0, Math.PI * 2, 16).every((p) => inside(p, fixed[0]));
  let close = t - DAB;
  while (close > 2 * DAB && !fitsDab(close)) close -= 0.5;
  if (!fitsDab(close)) return null;
  let b = H * 0.5;
  const maxB = avail.h - H - t;
  const bottomAt = (d: number) => bottomFlap(W, H, d, inset);
  while (b < maxB && !covered({ W, H }, [...fixed, foldOf(bottomAt(b), [[W, H], [0, H]])])) b += 1;
  if (b >= maxB) return null;
  // 4 mm more for sure - and enough to reach 10 mm past the dab, which glues to it
  b = Math.max(b + 4, H - close + DAB + 10);
  if (b > maxB) return null;
  // folded up, the bottom flap must stay within the envelope: a long card under a shallow flap
  // (the hexagonal packet under the heart, 2026-10-25) would need it to reach past the top edge
  if (b > H + 0.5) return null;
  const bottom = bottomAt(b);
  const hingeBottom: [Pt, Pt] = [[W, H], [0, H]];

  // the outline, round the panel: top flap, right flap, bottom flap, left flap
  const blankLocal = dedupe([...top, ...right.slice(1), ...bottom.slice(1), ...left.slice(1, -1)]);
  const xs = blankLocal.map((p) => p[0]), ys = blankLocal.map((p) => p[1]);
  const P = place(Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys));
  if (!P) return null;
  const at = (p: Pt) => apply(P, p);
  const atAll = (p: Pt[]) => p.map(at);

  // glue: where the folded bottom flap lies over each folded side flap, back on the bottom flap
  const bottomFolded = foldOf(bottom, hingeBottom);
  const glueLocal = [fixed[1], fixed[2]].map((side) => clipConvex(bottomFolded, side))
    .filter((g) => g.length >= 3 && area(g) > 40)
    .map((g) => shrink(g.map((p) => apply(reflectAcross(hingeBottom), p)), 1.5));
  // the top flap's tip lands on the bottom flap: a dab of glue there (or the seal on the outside)
  const tipFolded: Pt = [W / 2, close];
  const tip = apply(reflectAcross(hinges.top), tipFolded);
  const dab = arc(tip, DAB - 1, DAB - 1, 0, Math.PI * 2, 20).slice(0, -1);

  const blank = atAll(blankLocal);
  const sealR = 15;
  // the heart flap is its own seal
  const sealC = style === 'heart' ? null : sealSpot(blank, sealR);
  const P1 = invert(P);
  const M = mirrorX(SHEET.w);
  const flaps: Flap[] = [
    { id: 'left', poly: atAll(left), hinge: [at(hinges.left[0]), at(hinges.left[1])] },
    { id: 'right', poly: atAll(right), hinge: [at(hinges.right[0]), at(hinges.right[1])] },
    { id: 'bottom', poly: atAll(bottom), hinge: [at(hingeBottom[0]), at(hingeBottom[1])] },
    { id: 'top', poly: atAll(top), hinge: [at(hinges.top[0]), at(hinges.top[1])] },
  ];
  const panel = atAll(panelPoly);
  /** The flap side, seen as it lies on the table: the inside of the panel, then the flaps in the order they fold. */
  const back: View = {
    outline: panelPoly,
    layers: [
      { side: 1, clip: panel, toView: P1 },
      ...flaps.map((f) => ({ side: 2 as const, clip: f.poly.map((p) => apply(M, p)), toView: compose(P1, compose(reflectAcross(f.hinge), M)) })),
    ],
  };
  /** The address side: the envelope turned over, side to side. */
  const turnOver: Affine = [-1, 0, 0, 1, W, 0];
  const address: View = { outline: panelPoly, layers: [{ side: 2, clip: panel.map((p) => apply(M, p)), toView: compose(turnOver, compose(P1, M)) }] };
  const cardLocal: Pt[] = [[AIR, AIR], [W - AIR, AIR], [W - AIR, H - AIR], [AIR, H - AIR]];

  return {
    id, sheet: SHEET, card,
    cut: [blank, ...(sealC ? [arc(sealC, sealR, sealR, 0, Math.PI * 2, 48).slice(0, -1)] : [])],
    folds: [
      { line: [at(hinges.left[0]), at(hinges.left[1])], step: 2 },
      { line: [at(hinges.right[0]), at(hinges.right[1])], step: 2 },
      { line: [at(hingeBottom[0]), at(hingeBottom[1])], step: 3 },
      { line: [at(hinges.top[0]), at(hinges.top[1])], step: 5 },
    ],
    glue: [...glueLocal.map(atAll), atAll(dab)],
    cardAt: atAll(cardLocal),
    seal: sealC ? { c: sealC, r: sealR } : null,
    address, back,
    nameAt: { c: [W / 2, H * 0.58], w: W * 0.7, h: H * 0.4 },
    sealAt: tipFolded,
    tipArt: style === 'heart' ? heartShape(W, t).map(([x, y]): Pt => [x, -y]) : null,
    closed: { w: W, h: H },
    inside: { w: W, h: H },
    scissors: true,
    steps: ['envStepCut', 'envStepSides', 'envStepBottom', 'envStepCardIn', 'envStepClose'],
  };
}

/* ---------------------------------------------------------- the pillow */

function pillow(card: { w: number; h: number }): Envelope | null {
  const F = card.h + 2 * AIR + 4;                 // each face: the card's height, air, and room to bow
  const inner = card.w + 2 * AIR;                 // along the tube: the card's length and air
  const avail = { w: SHEET.w - 2 * MARGIN, h: SHEET.h - 2 * MARGIN };
  const e = Math.min(F * 0.22, (avail.w - inner) / 4);
  if (e < 6 || 2 * F + SEAM > avail.h) return null;
  const L = inner + 2 * e;                        // the faces' straight edges; the creases bow in by e at each end
  // local frame: the tube's length along x (0..L), faces A (y 0..F) and B (F..2F), the seam strip under them
  const face = (y0: number) => ({
    leftCut: sagArc([0, y0 + F], [0, y0], e),       // bulges out (to -x)
    leftCrease: sagArc([0, y0 + F], [0, y0], -e),   // bulges in
    rightCut: sagArc([L, y0], [L, y0 + F], e),
    rightCrease: sagArc([L, y0], [L, y0 + F], -e),
  });
  const A = face(0), B = face(F);
  const seamTop = 2 * F, seamIn = 4;
  const blankLocal: Pt[] = dedupe([
    [0, 0], [L, 0],
    ...A.rightCut.slice(1), ...B.rightCut.slice(1),
    [L - seamIn, seamTop + SEAM], [seamIn, seamTop + SEAM],
    ...B.leftCut, ...A.leftCut.slice(1, -1),
  ]);
  const xs = blankLocal.map((p) => p[0]), ys = blankLocal.map((p) => p[1]);
  const P = place(Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys));
  if (!P) return null;
  const at = (p: Pt) => apply(P, p), atAll = (p: Pt[]) => p.map(at);
  const P1 = invert(P), M = mirrorX(SHEET.w);
  // a face as it shows on the finished pillow: between its two curved creases
  const faceOutline = (f: typeof A): Pt[] => [...f.rightCrease, ...f.leftCrease];
  const faceA = faceOutline(A), faceB = faceOutline(B);
  // the front: face A, read from outside (side 2 as printed, turned over side to side into the envelope's frame)
  const turnOverA: Affine = [-1, 0, 0, 1, L, 0];
  const address: View = { outline: faceA, layers: [{ side: 2, clip: atAll(faceA).map((p) => apply(M, p)), toView: compose(turnOverA, compose(P1, M)) }] };
  // the back: face B, folded under face A along y = F, then the pillow turned over side to side
  const foldF = reflectAcross([at([0, F]), at([L, F])]);
  const backView: View = {
    outline: faceA,
    layers: [{ side: 2, clip: atAll(faceB).map((p) => apply(M, p)), toView: compose(turnOverA, compose(P1, compose(foldF, M))) }],
  };
  const cardLocal: Pt[] = [[e + AIR, (F - card.h) / 2], [L - e - AIR, (F - card.h) / 2], [L - e - AIR, (F + card.h) / 2], [e + AIR, (F + card.h) / 2]];
  return {
    id: 'pillow', sheet: SHEET, card,
    cut: [atAll(blankLocal)],
    folds: [
      { line: [at([0, F]), at([L, F])], step: 2 },
      { line: [at([0, 2 * F]), at([L, 2 * F])], step: 2 },
      ...[A, B].flatMap((f) => [
        { line: atAll(f.leftCrease), step: 2, curved: true },
        { line: atAll(f.rightCrease), step: 2, curved: true },
      ]),
    ],
    glue: [atAll(shrink([[seamIn + 2, 0], [L - seamIn - 2, 0], [L - seamIn - 2, SEAM], [seamIn + 2, SEAM]], 0.5))],
    cardAt: atAll(cardLocal),
    seal: null,
    address, back: backView,
    nameAt: { c: [L / 2, F * 0.5], w: L * 0.7, h: F * 0.45 },
    sealAt: null,
    tipArt: null,
    closed: { w: L, h: F },
    inside: { w: inner, h: F - 4 },
    scissors: true,
    steps: ['envStepCut', 'envStepCrease', 'envStepTube', 'envStepPillowEnds'],
  };
}

/* ------------------------------------------- no scissors: the pocket */

/**
 * The 2026-09-30 study's no-cut pocket (foldmycard OpenCode/2026-09-30-envelope-nocut.md):
 * the whole sheet; side strips folded in full height; the bottom folded up as the
 * front; the top folded down to close. Its sides are glued - the study left them
 * open - so the card cannot slip out sideways.
 */
function pocket(card: { w: number; h: number }): Envelope | null {
  const GAP = 10, TUCK = 30;
  const Wi = card.w + 2 * AIR;
  const seam = (SHEET.w - Wi) / 2;
  if (seam < 14) return null;
  const throat = GAP + TUCK;
  const backH = (SHEET.h - throat + GAP) / 2;
  const floor = throat + backH;
  if (backH < card.h + 2 * AIR) return null;
  const x0 = seam, x1 = seam + Wi;
  const M = mirrorX(SHEET.w);
  const panel: Pt[] = [[x0, throat], [x1, throat], [x1, floor], [x0, floor]];
  const front: Pt[] = [[x0, floor], [x1, floor], [x1, SHEET.h], [x0, SHEET.h]];
  const closer: Pt[] = [[x0, 0], [x1, 0], [x1, throat], [x0, throat]];
  const view = translate(-x0, -throat);
  const flaps: Flap[] = [
    { id: 'bottom', poly: front, hinge: [[x1, floor], [x0, floor]] },
    { id: 'top', poly: closer, hinge: [[x0, throat], [x1, throat]] },
  ];
  const panelLocal: Pt[] = [[0, 0], [Wi, 0], [Wi, backH], [0, backH]];
  const back: View = {
    outline: panelLocal,
    layers: [
      { side: 1, clip: panel, toView: view },
      ...flaps.map((f) => ({ side: 2 as const, clip: f.poly.map((p) => apply(M, p)), toView: compose(view, compose(reflectAcross(f.hinge), M)) })),
    ],
  };
  const address: View = { outline: panelLocal, layers: [{ side: 2, clip: panel.map((p) => apply(M, p)), toView: compose([-1, 0, 0, 1, Wi, 0], compose(view, M)) }] };
  const band = 12;
  const strip = (xa: number, xb: number): Pt[] => [[xa, throat + 2], [xb, throat + 2], [xb, SHEET.h - MARGIN], [xa, SHEET.h - MARGIN]];
  const cardTop = floor - AIR - card.h;
  return {
    id: 'pocket', sheet: SHEET, card,
    cut: [],
    folds: [
      { line: [[x0, MARGIN], [x0, SHEET.h - MARGIN]], step: 1 },
      { line: [[x1, MARGIN], [x1, SHEET.h - MARGIN]], step: 1 },
      { line: [[MARGIN, floor], [SHEET.w - MARGIN, floor]], step: 2 },
      { line: [[MARGIN, throat], [SHEET.w - MARGIN, throat]], step: 4 },
    ],
    glue: [strip(x0 + 1.5, x0 + band), strip(x1 - band, x1 - 1.5)],
    cardAt: [[x0 + AIR, cardTop], [x1 - AIR, cardTop], [x1 - AIR, floor - AIR], [x0 + AIR, floor - AIR]],
    seal: null,
    address, back,
    nameAt: { c: [Wi / 2, backH * 0.6], w: Wi * 0.7, h: backH * 0.4 },
    sealAt: [Wi / 2, throat - 12],
    tipArt: null,
    closed: { w: Wi, h: backH },
    inside: { w: Wi, h: backH },
    scissors: false,
    steps: ['envStepPocketSides', 'envStepPocketBottom', 'envStepCardIn', 'envStepPocketClose'],
  };
}

/**
 * An envelope for a closed card of this size (mm; either way round - it lies
 * sideways inside), or null when that envelope cannot come out of one sheet.
 */
export function envelopeFor(id: EnvelopeId, closedCard: { w: number; h: number }): Envelope | null {
  const card = { w: Math.max(closedCard.w, closedCard.h), h: Math.min(closedCard.w, closedCard.h) };
  if (id === 'pillow') return pillow(card);
  if (id === 'pocket') return pocket(card);
  return cutOut(id, card);
}

const fitting = new Map<string, boolean>();
/**
 * Whether any envelope fits a design's closed card, its front view: the photo frame
 * (2026-10-31), 165 x 229 mm folded, is too big for an envelope from one sheet, and
 * its pages then offer none.
 */
export function envelopeFits(template: { views: { id: string; widthMm: number; heightMm: number }[] }): boolean {
  const v = template.views.find((x) => x.id === 'f1');
  if (!v) return false;
  const key = `${v.widthMm}x${v.heightMm}`;
  if (!fitting.has(key)) fitting.set(key, ENVELOPES.some((id) => envelopeFor(id, { w: v.widthMm, h: v.heightMm }) !== null));
  return fitting.get(key)!;
}

/** The flap's polygon as it lies once folded, on the sheet (side-1 coordinates). */
export const folded = (f: Flap): Pt[] => f.poly.map((p) => apply(reflectAcross(f.hinge), p));

/** Whether a design takes glue. */
export const usesGlue = (e: Envelope) => e.glue.length > 0;
